/**
 * 조직도 페이지 (React Flow 기반) - V104
 *
 * **역할:**
 * - 조직 구조를 React Flow 노드/엣지로 시각화
 * - 편집 모드에서 노드 위치 조정 가능 (그리드 스냅)
 * - 레이아웃 저장/로드 기능
 *
 * **접근 권한:**
 * - 보기: 모든 로그인 사용자
 * - 편집: HR 또는 Admin만
 *
 * **비활성 조직 처리:**
 * - status !== 'active' 조직은 트리에서 제외됨
 * - 고아 조직(부모가 비활성인 active 조직)도 제외됨
 * - useOrgTree Hook에서 자동 필터링
 */

import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import {
  ReactFlow,
  Node,
  Edge,
  useNodesState,
  useEdgesState,
  Background,
  BackgroundVariant,
  Panel,
  NodeTypes,
  EdgeTypes,
  ConnectionMode,
  Handle,
  Position,
  PanOnScrollMode,
  ReactFlowInstance,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';

import {
  Network,
  Loader,
  AlertCircle,
  RefreshCw,
  Edit,
  Save,
  X,
} from 'lucide-react';

import { useOrgTree, OrgTreeNode } from '../../shared/hooks/useOrgTree';
import { useOrgTreeLayout, DEFAULT_GRID_SIZE } from '../../shared/hooks/useOrgTreeLayout';
import { useCurrentUser } from '../../shared/hooks/useCurrentUser';
import { useAuthContext } from '../../shared/hooks/useAuthContext';
import { useJobTitles } from '../../shared/hooks/useJobTitles';
import { isHrOrAdmin } from '../../utils/rbac';
import { OrgChartEmployee } from '../../shared/hooks/useOrgChartEmployees';
import { OrgNodePosition, OrgEdgePath } from '../../shared/types/orgTreeLayout';
import { OrgBendableEdge } from './OrgBendableEdge';
import { OrgChartMemberPanel } from './components/OrgChartMemberPanel';

// ============================================
// V40: 국가 선택 버튼 컴포넌트
// ============================================

/**
 * V40: 국가 선택 버튼 Props
 */
interface CompanyCodeButtonProps {
  code: 'KR' | 'CN';
  label: string;
  isSelected: boolean;
  onClick: () => void;
}

/**
 * V40: 국가 선택 버튼 컴포넌트
 */
function CompanyCodeButton({ code, label, isSelected, onClick }: CompanyCodeButtonProps) {
  return (
    <button
      onClick={onClick}
      style={{
        padding: '8px 16px',
        border: isSelected ? '2px solid var(--color-primary)' : '1px solid #e2e8f0',
        borderRadius: '8px',
        background: isSelected ? 'var(--color-primary)' : 'white',
        color: isSelected ? 'white' : '#64748b',
        fontSize: '14px',
        fontWeight: isSelected ? '600' : '500',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        boxShadow: isSelected ? '0 2px 4px rgba(59, 130, 246, 0.2)' : '0 1px 2px rgba(0,0,0,0.05)',
      }}
    >
      {label}
    </button>
  );
}

// ============================================
// 상수 정의 (V128: 노드 크기 추가 축소)
// ============================================

/** V128: 노드 기본 너비 (240px로 축소) */
const NODE_WIDTH = 240;

/** V128: 노드 최소 너비 (120px로 축소) */
const NODE_MIN_WIDTH = 120;

/** V128: 노드 최대 너비 */
const NODE_MAX_WIDTH = 360;

/** V128: 노드 기본 높이 (80px로 축소) */
const NODE_BASE_HEIGHT = 80;

/** V128: 노드 최소 높이 (60px로 축소) */
const NODE_MIN_HEIGHT = 60;

/** V137.6: 노드 최대 높이 (800px로 확대) */
const NODE_MAX_HEIGHT = 800;

/** V113.2: 정밀 사이즈 조절 단위 (px) */
const SIZE_STEP = 5;

/** V113.4: 롱프레스 연속 조절 딜레이 (ms) */
const LONGPRESS_DELAY = 400;
const LONGPRESS_INTERVAL = 80;

/**
 * V113.6: 조직 운영 역할자 기준 (jobTitles.order 기준)
 *
 * 담당(order=5)까지를 조직 운영 역할자로 분류합니다.
 * - 대표(1), 본부장(2), 실장(3), 팀장(4), 담당(5) → 조직 운영 역할자
 * - 팀원(6) → 일반 구성원
 *
 * 기준: jobTitle.order <= LEADER_ORDER_THRESHOLD
 */
const LEADER_ORDER_THRESHOLD = 5;

/**
 * V113.4: 5의 배수로 올림/내림 정규화
 */
const normalizeToStep = (value: number, step: number, direction: 'up' | 'down'): number => {
  if (value % step === 0) return value;
  if (direction === 'up') {
    return Math.ceil(value / step) * step;
  } else {
    return Math.floor(value / step) * step;
  }
};

/** V137.6: 구성원 1명당 추가 높이 (32px로 확대) */
const MEMBER_HEIGHT = 32;

/** V137.6: 구성원 목록 최대 높이 (NODE_MAX_HEIGHT와 동일) */
const MEMBER_LIST_MAX_HEIGHT = 800;

/** V128: 수평 간격 (40px로 축소) */
const HORIZONTAL_GAP = 40;

/** V128: 수직 간격 (60px로 축소) */
const VERTICAL_GAP = 60;

/**
 * 편집 모드 캔버스 제한 (V104.3)
 * - 좌측/상단: 0 기준 고정 (음수 이동 불가)
 * - 우측/하단: 동적 계산 (콘텐츠 + CANVAS_PADDING)
 */
const CANVAS_PADDING = 500;
const MIN_CANVAS_X = 2000;
const MIN_CANVAS_Y = 1500;

// ============================================
// 커스텀 노드 컴포넌트
// ============================================

/** 레이아웃 방향: TB=위→아래, LR=왼→오른 */
type LayoutDirection = 'TB' | 'LR';

interface OrgUnitNodeData extends Record<string, unknown> {
  orgUnit: OrgTreeNode['orgUnit'];
  members: OrgChartEmployee[];
  leader: OrgChartEmployee | undefined;
  totalMemberCount: number;
  isEditMode: boolean;
  layoutDirection: LayoutDirection;
  /** V113: 사용자 정의 노드 크기 */
  customWidth?: number;
  customHeight?: number;
  /** V113: 리사이즈 콜백 */
  onResize?: (width: number, height: number) => void;
  /** V113.6: 직책명 → order 매핑 (조직 운영 역할자 판단용) */
  jobTitleOrderMap: Map<string, number>;
  /** V55.14: 구성원 클릭 콜백 (사이드패널 표시용) */
  onMemberClick?: (memberId: string) => void;
}

/**
 * V113: 조직 박스 노드 컴포넌트
 * - 노드 크기 축소, 텍스트 확대
 * - 구성원 목록 스크롤 지원
 * - 편집 모드에서 리사이즈 핸들
 */
function OrgUnitNode({ data }: { data: OrgUnitNodeData; id: string }) {
  const { orgUnit, members, totalMemberCount, isEditMode, customWidth, customHeight, onResize, jobTitleOrderMap, onMemberClick } = data;

  /**
   * V113.6: 조직 운영 역할자 여부 판단 (order 기반)
   * - jobTitleOrderMap에서 직책명으로 order 조회
   * - order <= LEADER_ORDER_THRESHOLD (5) 이면 조직 운영 역할자
   */
  const isLeaderByOrder = (jobTitleName?: string): boolean => {
    if (!jobTitleName || !jobTitleOrderMap) return false;
    const order = jobTitleOrderMap.get(jobTitleName);
    if (order === undefined) return false;
    return order <= LEADER_ORDER_THRESHOLD;
  };

  // V113: 리사이즈 로직
  const nodeRef = React.useRef<HTMLDivElement>(null);

  // V116: 멤버 리스트 스크롤 ref
  const memberListRef = React.useRef<HTMLDivElement>(null);

  // V113.4: 롱프레스 interval ref
  const longPressTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const longPressIntervalRef = React.useRef<ReturnType<typeof setInterval> | null>(null);

  /**
   * V113.4: 5px 정규화 + 크기 변경 적용
   * - 현재값이 5의 배수가 아니면 먼저 정규화
   * - delta > 0: 올림 후 적용, delta < 0: 내림 후 적용
   */
  const applyNormalizedSizeChange = useCallback(
    (dimension: 'width' | 'height', delta: number) => {
      if (!isEditMode || !onResize) return;

      const currentWidth = customWidth || NODE_WIDTH;
      const currentHeight = customHeight || (nodeRef.current?.offsetHeight || NODE_BASE_HEIGHT);

      if (dimension === 'width') {
        // V113.4: 5의 배수가 아니면 먼저 정규화
        const normalizedWidth = normalizeToStep(currentWidth, SIZE_STEP, delta > 0 ? 'up' : 'down');
        const newWidth = Math.max(NODE_MIN_WIDTH, Math.min(NODE_MAX_WIDTH, normalizedWidth + delta));
        onResize(newWidth, currentHeight);
      } else {
        const normalizedHeight = normalizeToStep(currentHeight, SIZE_STEP, delta > 0 ? 'up' : 'down');
        const newHeight = Math.max(NODE_MIN_HEIGHT, Math.min(NODE_MAX_HEIGHT, normalizedHeight + delta));
        onResize(currentWidth, newHeight);
      }
    },
    [isEditMode, onResize, customWidth, customHeight]
  );

  // V131: 롱프레스용 현재 크기 ref (최신 상태 유지)
  const currentSizeRef = React.useRef({ width: customWidth || NODE_WIDTH, height: customHeight || NODE_BASE_HEIGHT });
  React.useEffect(() => {
    currentSizeRef.current = { width: customWidth || NODE_WIDTH, height: customHeight || (nodeRef.current?.offsetHeight || NODE_BASE_HEIGHT) };
  }, [customWidth, customHeight]);

  /**
   * V113.4: 롱프레스 시작 핸들러
   * - 즉시 1회 적용
   * - LONGPRESS_DELAY 후 LONGPRESS_INTERVAL마다 연속 적용
   */
  const handleSizeMouseDown = useCallback(
    (e: React.MouseEvent, dimension: 'width' | 'height', delta: number) => {
      e.preventDefault();
      e.stopPropagation();

      if (!isEditMode || !onResize) return;

      // 즉시 1회 적용
      applyNormalizedSizeChange(dimension, delta);

      // 롱프레스: DELAY 후 INTERVAL마다 연속 적용 (ref 사용으로 최신 상태 참조)
      longPressTimerRef.current = setTimeout(() => {
        longPressIntervalRef.current = setInterval(() => {
          const current = currentSizeRef.current;
          if (dimension === 'width') {
            const normalized = normalizeToStep(current.width, SIZE_STEP, delta > 0 ? 'up' : 'down');
            const newWidth = Math.max(NODE_MIN_WIDTH, Math.min(NODE_MAX_WIDTH, normalized + delta));
            currentSizeRef.current.width = newWidth;
            onResize(newWidth, current.height);
          } else {
            const normalized = normalizeToStep(current.height, SIZE_STEP, delta > 0 ? 'up' : 'down');
            const newHeight = Math.max(NODE_MIN_HEIGHT, Math.min(NODE_MAX_HEIGHT, normalized + delta));
            currentSizeRef.current.height = newHeight;
            onResize(current.width, newHeight);
          }
        }, LONGPRESS_INTERVAL);
      }, LONGPRESS_DELAY);
    },
    [isEditMode, onResize, applyNormalizedSizeChange]
  );

  /**
   * V113.4: 롱프레스 종료 핸들러
   */
  const handleSizeMouseUp = useCallback(() => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
    if (longPressIntervalRef.current) {
      clearInterval(longPressIntervalRef.current);
      longPressIntervalRef.current = null;
    }
  }, []);

  // V113.4: 컴포넌트 언마운트 시 cleanup
  React.useEffect(() => {
    return () => {
      if (longPressTimerRef.current) clearTimeout(longPressTimerRef.current);
      if (longPressIntervalRef.current) clearInterval(longPressIntervalRef.current);
    };
  }, []);

  const handleResizeMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (!isEditMode || !onResize) return;
      e.stopPropagation();
      e.preventDefault();

      const startX = e.clientX;
      const startY = e.clientY;
      const startWidth = customWidth || NODE_WIDTH;
      const startHeight = customHeight || (nodeRef.current?.offsetHeight || NODE_BASE_HEIGHT);

      const handleMouseMove = (moveEvent: MouseEvent) => {
        const deltaX = moveEvent.clientX - startX;
        const deltaY = moveEvent.clientY - startY;
        const newWidth = startWidth + deltaX;
        const newHeight = startHeight + deltaY;
        onResize(newWidth, newHeight);
      };

      const handleMouseUp = () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };

      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    },
    [isEditMode, onResize, customWidth, customHeight]
  );

  // 조직장 판정 (V106: orgManagerEmployeeId 우선, leaderId fallback)
  const leaderId = orgUnit.orgManagerEmployeeId || orgUnit.leaderId || null;

  // V113: Handle 스타일 (4방향 공통)
  const handleStyle = {
    background: '#94a3b8',
    width: '8px',
    height: '8px',
    border: '2px solid white',
  };

  // V113: 노드 크기 (사용자 정의 또는 기본값)
  const nodeWidth = customWidth || NODE_WIDTH;
  const nodeHeight = customHeight || undefined; // auto height

  return (
    <div
      ref={nodeRef}
      style={{
        background: 'white',
        borderRadius: '6px',
        boxShadow: isEditMode
          ? '0 2px 8px rgba(59, 130, 246, 0.25)'
          : '0 1px 4px rgba(0,0,0,0.06)',
        border: isEditMode ? '2px solid var(--color-primary)' : '1px solid #e2e8f0',
        padding: '6px',  // V128: padding 더 축소
        width: `${nodeWidth}px`,
        minWidth: `${NODE_MIN_WIDTH}px`,
        maxWidth: `${NODE_MAX_WIDTH}px`,
        height: nodeHeight ? `${nodeHeight}px` : 'auto',
        minHeight: `${NODE_MIN_HEIGHT}px`,
        maxHeight: `${NODE_MAX_HEIGHT}px`,
        cursor: isEditMode ? 'move' : 'default',
        position: 'relative',
        overflow: isEditMode ? 'visible' : 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* V113: 4방향 Handle */}
      <Handle
        type="target"
        position={Position.Top}
        id="top"
        style={{ ...handleStyle, top: '-4px' }}
      />
      <Handle
        type="source"
        position={Position.Bottom}
        id="bottom"
        style={{ ...handleStyle, bottom: '-4px' }}
      />
      <Handle
        type="target"
        position={Position.Left}
        id="left"
        style={{ ...handleStyle, left: '-4px' }}
      />
      <Handle
        type="source"
        position={Position.Right}
        id="right"
        style={{ ...handleStyle, right: '-4px' }}
      />

      {/* V128: 조직 헤더 (더 컴팩트) */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '4px',
          paddingBottom: '4px',
          borderBottom: '1px solid #e2e8f0',
          flexShrink: 0,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px', minWidth: 0 }}>
          <Network size={16} style={{ color: 'var(--color-primary)', flexShrink: 0 }} />
          <span
            style={{
              fontWeight: '600',
              fontSize: '14px',
              color: '#1e293b',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {orgUnit.name}
          </span>
        </div>
        <span
          style={{
            fontSize: '13px',
            fontWeight: '600',
            color: '#475569',
            background: '#f1f5f9',
            padding: '2px 6px',
            borderRadius: '4px',
            flexShrink: 0,
          }}
        >
          {totalMemberCount}명
        </span>
      </div>

      {/* V128: 구성원 목록 (더 컴팩트) */}
      <div
        ref={memberListRef}
        className="nodrag nopan nowheel"
        data-scrollzone="members"
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '2px',
          flex: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
          maxHeight: `${MEMBER_LIST_MAX_HEIGHT}px`,
          paddingRight: '2px',
          overscrollBehavior: 'contain',
        }}
      >
        {members.length === 0 && (
          <div
            style={{
              fontSize: '14px',
              color: '#94a3b8',
              textAlign: 'center',
              padding: '12px',
            }}
          >
            소속 구성원 없음
          </div>
        )}

        {/* V133: useOrgTree.getOrgMembers에서 이미 정렬됨 (조직장 → 직위순 → 입사일순) */}
        {members.map((member, idx) => {
            const isOrgManager = member.id === leaderId;
            const hasLeaderRole = isLeaderByOrder(member.jobTitle);
            // V113.6: 조직 운영 역할자면 직책(jobTitle), 아니면 직위(positionTitle)
            const displayTitle = hasLeaderRole
              ? member.jobTitle
              : member.positionTitle || '';

            return (
              <div
                key={member.id}
                className={onMemberClick && !isEditMode ? 'nodrag nopan' : undefined}
                onClick={onMemberClick && !isEditMode ? (e) => {
                  e.stopPropagation();
                  onMemberClick(member.id);
                } : undefined}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '4px 6px',  // V137.6: padding 확대
                  background: '#f8fafc',
                  borderRadius: '4px',
                  flexShrink: 0,
                  minHeight: '24px',  // V137.6: 행 높이 확대
                  cursor: onMemberClick && !isEditMode ? 'pointer' : 'default',
                  transition: 'background 0.15s ease',
                  // V55.14.1: pointer-events 명시적 설정으로 ReactFlow 이벤트 캡처 방지
                  pointerEvents: onMemberClick && !isEditMode ? 'auto' : 'inherit',
                }}
                onMouseEnter={onMemberClick && !isEditMode ? (e) => {
                  if (!isOrgManager) {
                    e.currentTarget.style.background = '#e2e8f0';
                  }
                } : undefined}
                onMouseLeave={onMemberClick && !isEditMode ? (e) => {
                  e.currentTarget.style.background = '#f8fafc';
                } : undefined}
              >
                {/* V137.6: 이름 + 직책 - 크기 확대 */}
                <span
                  style={{
                    fontSize: '13px',
                    fontWeight: '500',
                    // V66: 휴직중 — 옅은 주황 (#c2410c)
                    color: (member as any).status === 'on_leave' ? '#c2410c' : '#1e293b',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    flex: 1,
                    minWidth: 0,
                  }}
                >
                  {member.nameKo}
                  {(member as any).status === 'on_leave' && (
                    <span style={{ marginLeft: 4, padding: '1px 6px', borderRadius: 8, background: '#fff7ed', color: '#c2410c', border: '1px solid #fed7aa', fontSize: 10, fontWeight: 600 }}>
                      휴직중
                    </span>
                  )}
                </span>
                {/* V137.6: 직책/직위 크기 확대 */}
                <span
                  style={{
                    fontSize: '12px',
                    color: '#64748b',
                    fontWeight: '400',
                    flexShrink: 0,
                    marginLeft: '4px',
                  }}
                >
                  {displayTitle}
                </span>
              </div>
          );
        })}
      </div>

      {/* V128: 편집 모드 - 가로(width) 정밀 조절 UI (컴팩트) */}
      {isEditMode && onResize && (
        <div
          className="nodrag nopan"
          style={{
            position: 'absolute',
            top: '-44px',
            left: '50%',
            transform: 'translateX(-50%)',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            background: 'white',
            border: '1px solid #3b82f6',
            borderRadius: '6px',
            padding: '4px 8px',
            boxShadow: '0 2px 8px rgba(59,130,246,0.2)',
            zIndex: 100,
            pointerEvents: 'auto',
          }}
        >
          <button
            onMouseDown={(e) => handleSizeMouseDown(e, 'width', -SIZE_STEP)}
            onMouseUp={handleSizeMouseUp}
            onMouseLeave={handleSizeMouseUp}
            style={{
              width: '24px',
              height: '24px',
              border: '1px solid #cbd5e1',
              borderRadius: '4px',
              background: '#f8fafc',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '16px',
              fontWeight: 'bold',
              color: '#64748b',
              pointerEvents: 'auto',
            }}
            title="너비 -5px (길게 누르면 연속)"
          >
            −
          </button>
          <span
            style={{
              fontSize: '11px',
              color: '#1e293b',
              fontWeight: '600',
              minWidth: '50px',
              textAlign: 'center',
            }}
          >
            W:{nodeWidth}
          </span>
          <button
            onMouseDown={(e) => handleSizeMouseDown(e, 'width', SIZE_STEP)}
            onMouseUp={handleSizeMouseUp}
            onMouseLeave={handleSizeMouseUp}
            style={{
              width: '24px',
              height: '24px',
              border: '1px solid #cbd5e1',
              borderRadius: '4px',
              background: '#f8fafc',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '16px',
              fontWeight: 'bold',
              color: '#64748b',
              pointerEvents: 'auto',
            }}
            title="너비 +5px (길게 누르면 연속)"
          >
            +
          </button>
        </div>
      )}

      {/* V128: 편집 모드 - 세로(height) 정밀 조절 UI (컴팩트) */}
      {isEditMode && onResize && (
        <div
          className="nodrag nopan"
          style={{
            position: 'absolute',
            right: '-48px',
            top: '50%',
            transform: 'translateY(-50%)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '4px',
            background: 'white',
            border: '1px solid #3b82f6',
            borderRadius: '6px',
            padding: '6px 4px',
            boxShadow: '0 2px 8px rgba(59,130,246,0.2)',
            zIndex: 100,
            pointerEvents: 'auto',
          }}
        >
          <button
            onMouseDown={(e) => handleSizeMouseDown(e, 'height', -SIZE_STEP)}
            onMouseUp={handleSizeMouseUp}
            onMouseLeave={handleSizeMouseUp}
            style={{
              width: '24px',
              height: '24px',
              border: '1px solid #cbd5e1',
              borderRadius: '4px',
              background: '#f8fafc',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '16px',
              fontWeight: 'bold',
              color: '#64748b',
              pointerEvents: 'auto',
            }}
            title="높이 -5px (길게 누르면 연속)"
          >
            −
          </button>
          <span
            style={{
              fontSize: '10px',
              color: '#1e293b',
              fontWeight: '600',
              writingMode: 'vertical-rl',
              textOrientation: 'mixed',
              minHeight: '40px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            H:{nodeHeight || 'auto'}
          </span>
          <button
            onMouseDown={(e) => handleSizeMouseDown(e, 'height', SIZE_STEP)}
            onMouseUp={handleSizeMouseUp}
            onMouseLeave={handleSizeMouseUp}
            style={{
              width: '24px',
              height: '24px',
              border: '1px solid #cbd5e1',
              borderRadius: '4px',
              background: '#f8fafc',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '16px',
              fontWeight: 'bold',
              color: '#64748b',
              pointerEvents: 'auto',
            }}
            title="높이 +5px (길게 누르면 연속)"
          >
            +
          </button>
        </div>
      )}

    </div>
  );
}

// 노드 타입 등록
const nodeTypes: NodeTypes = {
  orgUnit: OrgUnitNode,
};

// V106: 엣지 타입 등록 (bendable = 꺾기 가능한 엣지)
const edgeTypes: EdgeTypes = {
  bendable: OrgBendableEdge,
};

// ============================================
// 트리 레이아웃 계산 함수
// ============================================

interface LayoutResult {
  nodes: Node[];
  edges: Edge[];
}

/**
 * 트리 노드의 너비 계산 (자식 너비 합 기반)
 */
function calculateNodeWidth(node: OrgTreeNode): number {
  if (node.children.length === 0) {
    return NODE_WIDTH;
  }

  const childrenWidth = node.children.reduce((sum, child) => {
    return sum + calculateNodeWidth(child) + HORIZONTAL_GAP;
  }, 0) - HORIZONTAL_GAP;

  return Math.max(NODE_WIDTH, childrenWidth);
}

/**
 * V113: 노드 실제 높이 계산 (스크롤 영역으로 변경)
 * - 구성원 목록은 스크롤로 표시하므로 고정 높이 사용
 */
function calculateNodeHeight(memberCount: number): number {
  // 구성원이 0명일 때 "소속 구성원 없음" 메시지 높이
  const emptyMessageHeight = memberCount === 0 ? 50 : 0;
  // 구성원이 있을 때 스크롤 영역 최대 높이 사용
  const membersAreaHeight = memberCount > 0 ? Math.min(memberCount * MEMBER_HEIGHT, MEMBER_LIST_MAX_HEIGHT) : 0;

  return NODE_BASE_HEIGHT + membersAreaHeight + emptyMessageHeight;
}

/**
 * V108: 각 depth별 최대 노드 높이 계산
 */
function calculateDepthHeights(
  treeData: OrgTreeNode[],
  getOrgMembers: (orgUnitId: string) => OrgChartEmployee[]
): number[] {
  const depthHeights: number[] = [];

  function traverse(node: OrgTreeNode, depth: number) {
    const members = getOrgMembers(node.orgUnit.id);
    const nodeHeight = calculateNodeHeight(members.length);

    if (depthHeights[depth] === undefined || nodeHeight > depthHeights[depth]) {
      depthHeights[depth] = nodeHeight;
    }

    for (const child of node.children) {
      traverse(child, depth + 1);
    }
  }

  for (const root of treeData) {
    traverse(root, 0);
  }

  return depthHeights;
}

/**
 * 트리 레이아웃 자동 계산
 * @param layoutDirection 'TB' = 위→아래, 'LR' = 왼→오른
 * @param jobTitleOrderMap V113.6: 직책명 → order 매핑
 */
function calculateTreeLayout(
  treeData: OrgTreeNode[],
  getOrgMembers: (orgUnitId: string) => OrgChartEmployee[],
  savedLayout: Record<string, OrgNodePosition> | null,
  isEditMode: boolean,
  layoutDirection: LayoutDirection = 'TB',
  jobTitleOrderMap: Map<string, number> = new Map()
): LayoutResult {
  const nodes: Node[] = [];
  const edges: Edge[] = [];

  // V108: 각 depth별 최대 높이 사전 계산
  const depthHeights = calculateDepthHeights(treeData, getOrgMembers);

  // V108: 각 depth의 Y 시작 위치 계산 (누적)
  const depthYPositions: number[] = [0];
  for (let i = 0; i < depthHeights.length; i++) {
    depthYPositions[i + 1] = depthYPositions[i] + depthHeights[i] + VERTICAL_GAP;
  }

  // 루트 노드들의 시작 X 좌표
  let currentX = 0;

  /**
   * 재귀적으로 노드/엣지 생성
   */
  function layoutNode(
    treeNode: OrgTreeNode,
    depth: number,
    startX: number,
    parentId: string | null
  ): number {
    const orgUnitId = treeNode.orgUnit.id;
    const members = getOrgMembers(orgUnitId);
    const nodeWidth = calculateNodeWidth(treeNode);

    // 저장된 위치가 있으면 사용, 없으면 자동 계산
    let x: number, y: number;
    let customWidth: number | undefined;
    let customHeight: number | undefined;

    if (savedLayout && savedLayout[orgUnitId]) {
      x = savedLayout[orgUnitId].x;
      y = savedLayout[orgUnitId].y;
      // V113: 저장된 크기 복원
      customWidth = savedLayout[orgUnitId].width;
      customHeight = savedLayout[orgUnitId].height;
    } else {
      // V108: 자동 위치 - depth별 누적 Y 위치 사용 (겹침 방지)
      x = startX + (nodeWidth - NODE_WIDTH) / 2;
      y = depthYPositions[depth];
    }

    // 노드 생성
    nodes.push({
      id: orgUnitId,
      type: 'orgUnit',
      position: { x, y },
      data: {
        orgUnit: treeNode.orgUnit,
        members,
        leader: treeNode.leader,
        totalMemberCount: treeNode.totalMemberCount,
        isEditMode,
        layoutDirection,
        // V113: 저장된 노드 크기
        customWidth,
        customHeight,
        // V113.6: 직책 order 매핑 (조직 운영 역할자 판단용)
        jobTitleOrderMap,
      } as OrgUnitNodeData,
      draggable: isEditMode,
    });

    // V106: 부모-자식 엣지 생성 (레이아웃 방향에 따라 Handle 선택)
    // TB: 부모 bottom → 자식 top
    // LR: 부모 right → 자식 left
    if (parentId) {
      const sourceHandle = layoutDirection === 'TB' ? 'bottom' : 'right';
      const targetHandle = layoutDirection === 'TB' ? 'top' : 'left';

      edges.push({
        id: `${parentId}-${orgUnitId}`,
        source: parentId,
        target: orgUnitId,
        sourceHandle,
        targetHandle,
        type: 'bendable', // V106: 꺾기 가능한 커스텀 엣지
        style: { stroke: '#94a3b8', strokeWidth: 2 },
        // V128: 화살표 제거 - 직선 끝
        data: {
          bendPoints: [], // 기본값, 나중에 저장된 값으로 대체
          isEditMode,
        },
      });
    }

    // 자식 노드 배치
    let childX = startX;
    for (const child of treeNode.children) {
      const childWidth = calculateNodeWidth(child);
      layoutNode(child, depth + 1, childX, orgUnitId);
      childX += childWidth + HORIZONTAL_GAP;
    }

    return nodeWidth;
  }

  // 최상위 노드들 배치
  for (const rootNode of treeData) {
    const rootWidth = calculateNodeWidth(rootNode);
    layoutNode(rootNode, 0, currentX, null);
    currentX += rootWidth + HORIZONTAL_GAP * 2;
  }

  return { nodes, edges };
}

// ============================================
// 메인 컴포넌트
// ============================================

export function OrgChartPage() {
  // ============================================
  // 상태 및 Hook
  // ============================================

  const { user, isLoading: isLoadingUser } = useCurrentUser();
  const authContext = useAuthContext();

  // ============================================
  // V40: 국가 선택 및 편집 권한 로직
  // ============================================

  // V40: 사용자의 companyCode 확인 (users 문서에서 가져옴)
  const userCompanyCode = useMemo(() => {
    return (user?.profile as any)?.companyCode as 'KR' | 'CN' | undefined;
  }, [user?.profile]);

  // V40: 초기 국가 선택은 사용자의 companyCode (없으면 KR 기본값)
  const [selectedCompanyCode, setSelectedCompanyCode] = useState<'KR' | 'CN'>('KR');

  // V40: 사용자 companyCode가 로드되면 초기값 설정
  useEffect(() => {
    if (userCompanyCode) {
      setSelectedCompanyCode(userCompanyCode);
    }
  }, [userCompanyCode]);

  // V40: roleAssignments에서 roleId 추출
  const userRoleIds = useMemo(() => {
    return authContext?.roleAssignments?.map((ra) => ra.roleId) || [];
  }, [authContext?.roleAssignments]);

  // V40: 시스템 관리자 여부 (양쪽 모두 편집 가능)
  const isSystemAdmin = useMemo(() => {
    return userRoleIds.includes('system_admin');
  }, [userRoleIds]);

  // V40: HR 또는 회사별 관리자 여부
  const isHR = useMemo(() => userRoleIds.includes('role_hr'), [userRoleIds]);
  const isKRAdmin = useMemo(() => userRoleIds.includes('role_admin_kr'), [userRoleIds]);
  const isCNAdmin = useMemo(() => userRoleIds.includes('role_admin_cn'), [userRoleIds]);

  // V40: 선택된 회사의 조직도 편집 가능 여부
  // - system_admin: 양쪽 모두 편집 가능
  // - role_hr + 본인 회사: 편집 가능
  // - role_admin_kr: KR만 편집 가능
  // - role_admin_cn: CN만 편집 가능
  const canEditSelectedCompany = useMemo(() => {
    if (!user) return false;

    // system_admin은 모두 편집 가능
    if (isSystemAdmin) return true;

    // 본인 회사 체크
    const isOwnCompany = userCompanyCode === selectedCompanyCode;

    // HR은 본인 회사만 편집 가능
    if (isHR && isOwnCompany) return true;

    // 회사별 관리자는 해당 회사만 편집 가능
    if (isKRAdmin && selectedCompanyCode === 'KR') return true;
    if (isCNAdmin && selectedCompanyCode === 'CN') return true;

    return false;
  }, [user, isSystemAdmin, isHR, isKRAdmin, isCNAdmin, userCompanyCode, selectedCompanyCode]);

  // V40: 버튼 순서 - 본인 회사가 앞으로
  const companyButtons = useMemo(() => {
    const kr = { code: 'KR' as const, label: '한국지사' };
    const cn = { code: 'CN' as const, label: '대련지사' };

    if (userCompanyCode === 'CN') {
      return [cn, kr];
    }
    return [kr, cn]; // KR 또는 미지정은 KR 우선
  }, [userCompanyCode]);

  // V40: useOrgTree에 선택된 국가 전달 (항상 필터링)
  const { treeData, getOrgMembers, isLoading: isLoadingTree, error: treeError } = useOrgTree({
    requestedCompanyCode: selectedCompanyCode,
  });
  const { layout, isLoading: isLoadingLayout, isSaving, saveLayout, refetch: refetchLayout } =
    useOrgTreeLayout();
  // V113.6: 직책 마스터 조회 (order 기반 조직 운영 역할자 판단용)
  const { jobTitles, isLoading: isLoadingJobTitles } = useJobTitles({ status: 'active' });

  const [isEditMode, setIsEditMode] = useState(false);
  const [nodes, setNodes, onNodesChange] = useNodesState<Node>([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge>([]);

  // V106: 레이아웃 방향 (TB=위→아래, LR=왼→오른)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [layoutDirection, setLayoutDirection] = useState<LayoutDirection>('TB');

  // V105: 편집 모드 진입 시 고정된 캔버스 범위 (노드 이동으로 확장 안 됨)
  const [frozenCanvasBounds, setFrozenCanvasBounds] = useState<{ maxX: number; maxY: number } | null>(null);

  // V55.14: 사이드패널에 표시할 구성원 ID
  const [selectedMemberId, setSelectedMemberId] = useState<string | null>(null);

  // V114.7: ReactFlow 인스턴스 ref (편집 모드 저장용으로만 사용)
  const reactFlowInstanceRef = useRef<ReactFlowInstance | null>(null);

  const isLoading = isLoadingTree || isLoadingLayout || isLoadingUser || isLoadingJobTitles;
  const error = treeError;

  // V113.6: 직책명 → order 매핑 생성
  const jobTitleOrderMap = useMemo(() => {
    const map = new Map<string, number>();
    jobTitles.forEach((jt) => {
      map.set(jt.name, jt.order);
    });
    return map;
  }, [jobTitles]);

  // ============================================
  // 권한 체크 (V40: canEditSelectedCompany 사용)
  // ============================================

  // V40: canEdit는 이제 canEditSelectedCompany를 참조
  // 기존 isHrOrAdmin 로직은 레거시로 유지하되, 실제 편집 가능 여부는 canEditSelectedCompany 사용
  const canEdit = canEditSelectedCompany;

  // ============================================
  // 레이아웃 초기화/업데이트
  // ============================================

  /**
   * V106: bendPoints 변경 콜백 (엣지 꺾기 편집 시 호출)
   */
  const handleBendPointsChange = useCallback(
    (edgeId: string, newPoints: Array<{ x: number; y: number }>) => {
      setEdges((eds) =>
        eds.map((edge) => {
          if (edge.id === edgeId) {
            return {
              ...edge,
              data: {
                ...edge.data,
                bendPoints: newPoints,
              },
            };
          }
          return edge;
        })
      );
    },
    [setEdges]
  );

  /**
   * V114: 세로 오프셋 변경 콜백 (엣지 세로 길이 조절 시 호출)
   */
  const handleVerticalOffsetChange = useCallback(
    (edgeId: string, newOffset: number) => {
      setEdges((eds) =>
        eds.map((edge) => {
          if (edge.id === edgeId) {
            return {
              ...edge,
              data: {
                ...edge.data,
                verticalOffset: newOffset,
              },
            };
          }
          return edge;
        })
      );
    },
    [setEdges]
  );

  /**
   * V113.2: 노드 리사이즈 핸들러 (ref로 변환하여 순환 참조 방지)
   * - 편집 모드에서만 동작
   * - 최소/최대 크기 제한 + 그리드 스냅
   * - onResize 콜백 재주입으로 버튼 클릭 후에도 동작 유지
   */
  const handleNodeResizeRef = React.useRef<((nodeId: string, width: number, height: number) => void) | undefined>(undefined);

  handleNodeResizeRef.current = (nodeId: string, width: number, height: number) => {
    if (!isEditMode) return;

    // V113.3: 크기 제한만 적용 (그리드 스냅 제거 - 버튼으로 정밀 조절 가능하게)
    const clampedWidth = Math.max(NODE_MIN_WIDTH, Math.min(NODE_MAX_WIDTH, width));
    const clampedHeight = Math.max(NODE_MIN_HEIGHT, Math.min(NODE_MAX_HEIGHT, height));

    setNodes((nds) =>
      nds.map((n) => {
        if (n.id === nodeId) {
          return {
            ...n,
            data: {
              ...n.data,
              customWidth: clampedWidth,
              customHeight: clampedHeight,
              // V113.3: onResize 콜백 재주입
              onResize: (w: number, h: number) => handleNodeResizeRef.current?.(nodeId, w, h),
            },
          };
        }
        return n;
      })
    );
  };

  useEffect(() => {
    if (!isLoading && treeData.length > 0) {
      const savedNodes = layout?.nodes || null;
      const savedEdges = layout?.edges || [];

      const { nodes: layoutNodes, edges: layoutEdges } = calculateTreeLayout(
        treeData,
        getOrgMembers,
        savedNodes,
        isEditMode,
        layoutDirection,
        jobTitleOrderMap  // V113.6: 직책 order 매핑 전달
      );

      // V106: 저장된 bendPoints 적용 및 onBendPointsChange 콜백 추가
      const edgesWithBendPoints = layoutEdges.map((edge) => {
        // 저장된 엣지에서 bendPoints 찾기
        const savedEdge = savedEdges.find(
          (se) => se.source === edge.source && se.target === edge.target
        );

        return {
          ...edge,
          data: {
            ...edge.data,
            bendPoints: savedEdge?.bendPoints || [],
            // V114: 세로 오프셋 로드 및 콜백 주입
            verticalOffset: savedEdge?.verticalOffset || 0,
            isEditMode,
            onBendPointsChange: handleBendPointsChange,
            onVerticalOffsetChange: handleVerticalOffsetChange,
          },
        };
      });

      // V113.3: 노드에 isEditMode + onResize 콜백 주입
      // V55.14: onMemberClick 콜백 추가
      const nodesWithResize = layoutNodes.map((node) => ({
        ...node,
        data: {
          ...node.data,
          isEditMode, // V113.3: 현재 isEditMode 값 명시적 주입
          onResize: (width: number, height: number) => handleNodeResizeRef.current?.(node.id, width, height),
          onMemberClick: (memberId: string) => setSelectedMemberId(memberId), // V55.14
        },
      }));

      setNodes(nodesWithResize);
      setEdges(edgesWithBendPoints);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [treeData, layout, isLoading, isEditMode, layoutDirection, handleBendPointsChange, handleVerticalOffsetChange, jobTitleOrderMap]);

  // ============================================
  // 이벤트 핸들러
  // ============================================

  /**
   * V105: 현재 노드 기반 캔버스 범위 계산 (고정용)
   */
  const calculateCanvasBounds = useCallback(() => {
    if (nodes.length === 0) {
      return { maxX: MIN_CANVAS_X, maxY: MIN_CANVAS_Y };
    }

    let maxX = 0;
    let maxY = 0;

    nodes.forEach((node) => {
      const nodeRight = node.position.x + NODE_WIDTH;
      const nodeBottom = node.position.y + NODE_BASE_HEIGHT + MEMBER_HEIGHT * 5;
      if (nodeRight > maxX) maxX = nodeRight;
      if (nodeBottom > maxY) maxY = nodeBottom;
    });

    return {
      maxX: Math.max(maxX + CANVAS_PADDING, MIN_CANVAS_X),
      maxY: Math.max(maxY + CANVAS_PADDING, MIN_CANVAS_Y),
    };
  }, [nodes]);

  /**
   * 편집 모드 진입 - V105: 캔버스 범위를 이 시점에 고정
   */
  const handleEnterEditMode = () => {
    if (canEdit) {
      // 편집 모드 진입 시점의 캔버스 범위 고정
      setFrozenCanvasBounds(calculateCanvasBounds());
      setIsEditMode(true);
    }
  };

  /**
   * 편집 모드 취소 - V105: frozenCanvasBounds 리셋
   */
  const handleCancelEditMode = () => {
    setIsEditMode(false);
    setFrozenCanvasBounds(null);
    // 레이아웃 재로드하여 원래 상태로 복원
    refetchLayout();
  };

  /**
   * 레이아웃 저장 (V106: bendPoints 포함, V113: width/height 포함)
   */
  const handleSaveLayout = async () => {
    try {
      // V113.1: 현재 노드 위치 + 크기 수집 (undefined 필드 제외)
      const nodePositions: OrgNodePosition[] = nodes.map((node) => {
        const nodeData = node.data as OrgUnitNodeData | undefined;
        const pos: OrgNodePosition = {
          orgUnitId: node.id,
          x: node.position.x,
          y: node.position.y,
        };
        // V113.1: Firestore는 undefined 저장 불가 → 조건부 포함
        if (nodeData?.customWidth != null) {
          pos.width = nodeData.customWidth;
        }
        if (nodeData?.customHeight != null) {
          pos.height = nodeData.customHeight;
        }
        return pos;
      });

      // V106: 엣지 경로 수집 (bendPoints 포함), V114: verticalOffset 추가
      const edgePaths: OrgEdgePath[] = edges.map((edge) => {
        const edgeData = edge.data as {
          bendPoints?: Array<{ x: number; y: number }>;
          verticalOffset?: number;
        } | undefined;

        const path: OrgEdgePath = {
          source: edge.source,
          target: edge.target,
          bendPoints: edgeData?.bendPoints || [],
        };

        // V114: verticalOffset이 0이 아닌 경우만 저장
        if (edgeData?.verticalOffset != null && edgeData.verticalOffset !== 0) {
          path.verticalOffset = edgeData.verticalOffset;
        }

        return path;
      });

      // V114.2: 현재 viewport 수집 (zoom은 항상 1로 저장 - 확대 상태 저장 방지)
      const currentViewport = reactFlowInstanceRef.current?.getViewport();

      await saveLayout({
        nodes: nodePositions,
        edges: edgePaths,
        gridSize: DEFAULT_GRID_SIZE,
        // V114.2: viewport 저장 시 zoom은 min(zoom, 1)로 clamp
        viewport: currentViewport ? {
          x: currentViewport.x,
          y: currentViewport.y,
          zoom: Math.min(currentViewport.zoom, 1),
        } : undefined,
      });

      setIsEditMode(false);
      setFrozenCanvasBounds(null);  // V105: 저장 후 리셋
      alert('레이아웃이 저장되었습니다.');
    } catch (err: any) {
      console.error('[OrgChartPage] 레이아웃 저장 실패:', err);
      alert(err?.message || '레이아웃 저장에 실패했습니다.');
    }
  };

  /**
   * V105: 캔버스 범위
   * - 편집 모드: frozenCanvasBounds 사용 (노드 이동으로 확장 안 됨)
   * - 보기 모드: 동적 계산
   */
  const canvasBounds = useMemo(() => {
    // 편집 모드에서는 고정된 범위 사용
    if (isEditMode && frozenCanvasBounds) {
      return frozenCanvasBounds;
    }

    // 보기 모드에서는 동적 계산
    if (nodes.length === 0) {
      return { maxX: MIN_CANVAS_X, maxY: MIN_CANVAS_Y };
    }

    let maxX = 0;
    let maxY = 0;

    nodes.forEach((node) => {
      const nodeRight = node.position.x + NODE_WIDTH;
      const nodeBottom = node.position.y + NODE_BASE_HEIGHT + MEMBER_HEIGHT * 5;
      if (nodeRight > maxX) maxX = nodeRight;
      if (nodeBottom > maxY) maxY = nodeBottom;
    });

    return {
      maxX: Math.max(maxX + CANVAS_PADDING, MIN_CANVAS_X),
      maxY: Math.max(maxY + CANVAS_PADDING, MIN_CANVAS_Y),
    };
  }, [nodes, isEditMode, frozenCanvasBounds]);

  /**
   * V104.3: 노드 변경 시 위치 clamp (이중 안전장치)
   * - 편집 모드에서만 동작
   * - 좌측/상단 0 미만 방지
   */
  const handleNodesChange = useCallback(
    (changes: any[]) => {
      if (!isEditMode) return;

      // position 변경이 있는 경우 clamp 적용
      const clampedChanges = changes.map((change) => {
        if (change.type === 'position' && change.position) {
          return {
            ...change,
            position: {
              x: Math.max(0, change.position.x),
              y: Math.max(0, change.position.y),
            },
          };
        }
        return change;
      });

      onNodesChange(clampedChanges);
    },
    [isEditMode, onNodesChange]
  );

  /**
   * 노드 드래그 종료 시 그리드 스냅 + clamp (V104.3)
   * - 좌측/상단 0 이하로 이동 불가
   * - 우측/하단 동적 제한
   */
  const onNodeDragStop = useCallback(
    (event: React.MouseEvent, node: Node) => {
      // 그리드 스냅
      let snappedX = Math.round(node.position.x / DEFAULT_GRID_SIZE) * DEFAULT_GRID_SIZE;
      let snappedY = Math.round(node.position.y / DEFAULT_GRID_SIZE) * DEFAULT_GRID_SIZE;

      // V104.3: clamp - 좌측/상단 0, 우측/하단 동적 제한
      snappedX = Math.max(0, Math.min(snappedX, canvasBounds.maxX - NODE_WIDTH));
      snappedY = Math.max(0, Math.min(snappedY, canvasBounds.maxY - NODE_BASE_HEIGHT));

      setNodes((nds) =>
        nds.map((n) => {
          if (n.id === node.id) {
            return {
              ...n,
              position: { x: snappedX, y: snappedY },
            };
          }
          return n;
        })
      );
    },
    [setNodes, canvasBounds]
  );

  // ============================================
  // 렌더링
  // ============================================

  return (
    <div style={{ height: '100%', width: '100%', display: 'flex', flexDirection: 'column', background: '#f9fafb' }}>
      {/* 로딩 상태 */}
      {isLoading && (
        <div
          style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '16px',
          }}
        >
          <Loader
            size={48}
            style={{
              color: 'var(--color-primary)',
              animation: 'spin 1s linear infinite',
            }}
          />
          <p style={{ fontSize: '16px', color: '#64748b' }}>조직도를 불러오는 중...</p>
        </div>
      )}

      {/* 에러 상태 */}
      {error && !isLoading && (
        <div
          style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '16px',
            padding: '24px',
          }}
        >
          <AlertCircle size={48} style={{ color: '#ef4444' }} />
          <div style={{ textAlign: 'center' }}>
            <h3 style={{ fontSize: '18px', fontWeight: 'bold', color: '#1e293b' }}>
              조직도를 불러오는 중 오류가 발생했습니다.
            </h3>
            <p style={{ fontSize: '14px', color: '#64748b', marginTop: '8px' }}>
              {error.message}
            </p>
          </div>
          <button
            onClick={() => window.location.reload()}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '10px 20px',
              background: '#ef4444',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer',
            }}
          >
            <RefreshCw size={16} />
            다시 시도
          </button>
        </div>
      )}

      {/* 빈 상태 */}
      {!isLoading && !error && treeData.length === 0 && (
        <div
          style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '16px',
          }}
        >
          <Network size={64} style={{ color: '#94a3b8' }} />
          <div style={{ textAlign: 'center' }}>
            <h3 style={{ fontSize: '18px', fontWeight: 'bold', color: '#1e293b' }}>
              조직 데이터가 없습니다.
            </h3>
            <p style={{ fontSize: '14px', color: '#64748b', marginTop: '8px' }}>
              조직관리 페이지에서 조직을 추가해 주세요.
            </p>
          </div>
        </div>
      )}

      {/* V122: ReactFlow 단일 구조 - 뷰/편집 모드 통합 */}
      {!isLoading && !error && treeData.length > 0 && (
        <div style={{ flex: 1, minHeight: 0 }}>
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={isEditMode ? handleNodesChange : undefined}
            onEdgesChange={isEditMode ? onEdgesChange : undefined}
            onNodeDragStop={isEditMode ? onNodeDragStop : undefined}
            nodeTypes={nodeTypes}
            edgeTypes={edgeTypes}
            connectionMode={ConnectionMode.Loose}
            defaultEdgeOptions={{
              type: 'bendable',
              style: { stroke: '#64748b', strokeWidth: 2 },
              animated: false,
            }}
            fitView={false}
            fitViewOptions={{ padding: 0.2 }}
            onInit={(instance) => {
              reactFlowInstanceRef.current = instance;
            }}
            snapToGrid={isEditMode}
            snapGrid={[DEFAULT_GRID_SIZE, DEFAULT_GRID_SIZE]}
            // V64: 뷰 모드에서도 패닝 허용 (하단 잘림 방지)
            // - 드래그/스크롤로 캔버스 이동 가능
            // - 노드 드래그/줌은 편집 모드에서만
            panOnDrag={true}
            panOnScroll={true}
            panOnScrollMode={PanOnScrollMode.Vertical}
            zoomOnScroll={isEditMode}
            zoomOnPinch={isEditMode}
            zoomOnDoubleClick={false}
            preventScrolling={true}
            nodesDraggable={isEditMode}
            nodesConnectable={false}
            elementsSelectable={isEditMode}
            translateExtent={[[0, 0], [canvasBounds.maxX, canvasBounds.maxY]]}
            nodeExtent={[[0, 0], [canvasBounds.maxX - NODE_WIDTH, canvasBounds.maxY - NODE_BASE_HEIGHT]]}
            // @ts-ignore
            proOptions={{ hideAttribution: true }}
          >
            {/* V131: 편집 모드에서만 배경 눈금선 표시 */}
            {isEditMode && (
              <Background
                variant={BackgroundVariant.Dots}
                gap={DEFAULT_GRID_SIZE}
                color="#94a3b8"
              />
            )}

            {/* V132: 좌상단 - 조직도 제목 + 편집 모드 표시 */}
            <Panel position="top-left">
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                }}
              >
                {/* 조직도 제목 */}
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    background: 'white',
                    padding: '8px 16px',
                    borderRadius: '8px',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                  }}
                >
                  <Network size={20} style={{ color: 'var(--color-primary)' }} />
                  <span style={{ fontSize: '16px', fontWeight: '600', color: '#1e293b' }}>
                    조직도
                  </span>
                </div>

                {/* V40: 국가 선택 버튼 (모든 사용자에게 표시, 본인 회사 버튼이 앞) */}
                <div
                  style={{
                    display: 'flex',
                    gap: '8px',
                    background: 'white',
                    padding: '4px',
                    borderRadius: '10px',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                  }}
                >
                  {companyButtons.map((btn) => (
                    <CompanyCodeButton
                      key={btn.code}
                      code={btn.code}
                      label={btn.label}
                      isSelected={selectedCompanyCode === btn.code}
                      onClick={() => setSelectedCompanyCode(btn.code)}
                    />
                  ))}
                </div>

                {/* V40: 편집 불가 안내 (타사 조직도 선택 시) */}
                {!canEditSelectedCompany && (isHR || isKRAdmin || isCNAdmin) && (
                  <div
                    style={{
                      background: '#fef3c7',
                      color: '#92400e',
                      padding: '6px 12px',
                      borderRadius: '6px',
                      fontSize: '12px',
                      fontWeight: '500',
                    }}
                  >
                    조회 전용 (타사)
                  </div>
                )}

                {/* 편집 모드 표시 */}
                {isEditMode && (
                  <div
                    style={{
                      background: 'var(--color-primary)',
                      color: 'white',
                      padding: '8px 16px',
                      borderRadius: '8px',
                      fontSize: '13px',
                      fontWeight: '500',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                    }}
                  >
                    <Edit size={14} />
                    편집 모드 - 노드 드래그로 위치 조정
                  </div>
                )}
              </div>
            </Panel>

            {/* V132: 우상단 - 편집/취소/저장 버튼 */}
            <Panel position="top-right">
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                }}
              >
                {!isEditMode ? (
                  // 보기 모드: 편집 모드 버튼
                  canEdit && (
                    <button
                      onClick={handleEnterEditMode}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        padding: '8px 16px',
                        background: 'var(--color-primary)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        fontSize: '13px',
                        fontWeight: '500',
                        cursor: 'pointer',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                      }}
                    >
                      <Edit size={14} />
                      편집 모드
                    </button>
                  )
                ) : (
                  // 편집 모드: 취소/저장 버튼
                  <>
                    <button
                      onClick={handleCancelEditMode}
                      disabled={isSaving}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        padding: '8px 16px',
                        background: 'white',
                        color: '#64748b',
                        border: '1px solid #e2e8f0',
                        borderRadius: '8px',
                        fontSize: '13px',
                        fontWeight: '500',
                        cursor: isSaving ? 'not-allowed' : 'pointer',
                        opacity: isSaving ? 0.5 : 1,
                        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                      }}
                    >
                      <X size={14} />
                      취소
                    </button>
                    <button
                      onClick={handleSaveLayout}
                      disabled={isSaving}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        padding: '8px 16px',
                        background: isSaving ? '#94a3b8' : '#22c55e',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        fontSize: '13px',
                        fontWeight: '500',
                        cursor: isSaving ? 'not-allowed' : 'pointer',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                      }}
                    >
                      {isSaving ? (
                        <>
                          <Loader size={14} style={{ animation: 'spin 1s linear infinite' }} />
                          저장 중...
                        </>
                      ) : (
                        <>
                          <Save size={14} />
                          저장
                        </>
                      )}
                    </button>
                  </>
                )}
              </div>
            </Panel>
          </ReactFlow>

          {/* V55.14: 구성원 상세 사이드패널 */}
          <OrgChartMemberPanel
            isOpen={selectedMemberId !== null}
            targetUserId={selectedMemberId}
            onClose={() => setSelectedMemberId(null)}
          />
        </div>
      )}

      {/* 스타일 */}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .react-flow {
          background: #f9fafb !important;
        }
      `}</style>
    </div>
  );
}

export default OrgChartPage;
